/**
 * The ingredient list's filtering and ordering, in one place so the table,
 * the phone rows, the header arrows and the tests all agree.
 *
 * Filtering mirrors GET /api/ingredients exactly (pleach/api/ingredients.py
 * list_ingredients and services/ingredients.py matches_search), because the
 * screen fetches the whole kitchen once and narrows it in the browser: a
 * segment switch or a keystroke answers on the next frame, and the numbers
 * on the segments (the server's `counts`) still match the rows under them.
 *   needs_price  not cost_basis_complete (no price, no pack size, an assumed
 *                density or a missing each weight: the dashboard's rule)
 *   unused       is_unused
 *   no group     no group set, or its group was deleted
 *   search       every word in the name, the variant or the supplier,
 *                ignoring case and accents ("creme" finds "Crème fraîche")
 *
 * Ordering: blanks go last whichever way a column is sorted (a missing
 * price is not the cheapest one), and ties fall back to the name.
 */
import type { Ingredient, IngredientNeed } from '@/api/queries/ingredients';

// ── The list's URL state ─────────────────────────────────────────────────

/** The list's segments: the handoff's All / Needs price / Unused. */
export type IngredientSegment = 'all' | 'needs_price' | 'unused';

/** The group select: every group, no usable group, or one group by id. */
export type GroupFilter = 'all' | 'none' | number;

/** /ingredients?filter=&group=&search=. Defaults are left out, so a plain
 *  /ingredients stays a plain URL. */
export type IngredientListSearch = {
  search?: string;
  filter?: Exclude<IngredientSegment, 'all'>;
  group?: Exclude<GroupFilter, 'all'>;
};

const truthy = (v: unknown) => v === true || v === 1 || v === '1' || v === 'true';

/** Reads the URL leniently: `needs-price` or `needs_price` (and the API's
 *  needs_size, a subset) open Needs price; the legacy unused_only=1 opens
 *  Unused; a group id may arrive as a number or a string.
 *
 *  Every key comes back, set to undefined when it is missing or makes no
 *  sense. TanStack Router lays a route's validated search over the raw one
 *  it parsed, so a key left out here would reach the screen raw: its JSON
 *  parse turns ?search=40 into the number 40 (which crashed the filter) and
 *  would pass ?filter=nonsense straight through. */
export function parseIngredientListSearch(s: Record<string, unknown>): IngredientListSearch {
  const raw = typeof s.search === 'string' ? s.search : typeof s.search === 'number' ? String(s.search) : '';
  const f = typeof s.filter === 'string' ? s.filter.trim().toLowerCase().replace(/-/g, '_') : '';
  const filter: IngredientListSearch['filter'] =
    f === 'needs_price' || f === 'needs_size'
      ? 'needs_price'
      : f === 'unused' || truthy(s.unused_only) || truthy(s.unused)
        ? 'unused'
        : undefined;
  const g = s.group;
  const group: IngredientListSearch['group'] =
    g === 'none'
      ? 'none'
      : typeof g === 'number' && Number.isInteger(g) && g > 0
        ? g
        : typeof g === 'string' && /^\d+$/.test(g) && Number(g) > 0
          ? Number(g)
          : undefined;
  return { search: raw.trim() ? raw : undefined, filter, group };
}

// ── Filtering ────────────────────────────────────────────────────────────

/** Case- and accent-insensitive form for matching (the server's `fold`). */
export function fold(text: string | number | null | undefined): string {
  if (text === null || text === undefined || text === '') return '';
  return String(text).normalize('NFKD').replace(/\p{M}/gu, '').toLowerCase();
}

/** The words to match. The name join and stray punctuation are not words,
 *  so a pasted "Butter — Unsalted" finds Butter — Unsalted (the same rule as
 *  the API's services/ingredients.py search_tokens). */
export function searchTokens(search: string | null | undefined): string[] {
  return fold(search)
    .split(/\s+/)
    .map((t) => t.replace(/^[,;:!?()"'—–]+|[,;:!?()"'—–]+$/g, ''))
    .filter((t) => /[\p{L}\p{N}]/u.test(t));
}

/** Every word must appear in the name, the variant or the supplier, so
 *  "unsalted butter" and "bidfood butter" both find Butter — Unsalted. */
export function matchesSearch(item: Pick<Ingredient, 'name' | 'variant' | 'supplier'>, tokens: string[]): boolean {
  if (tokens.length === 0) return true;
  const fields = [fold(item.name), fold(item.variant), fold(item.supplier)];
  return tokens.every((t) => fields.some((f) => f.includes(t)));
}

export function inSegment(item: Pick<Ingredient, 'cost_basis_complete' | 'is_unused'>, segment: IngredientSegment): boolean {
  if (segment === 'needs_price') return !item.cost_basis_complete;
  if (segment === 'unused') return item.is_unused;
  return true;
}

/** True when the item has no group a chef can pick: none set, or its group
 *  was deleted (the FK is kept, but the group is not usable). */
export function hasNoGroup(item: Pick<Ingredient, 'group_id' | 'group_deleted'>): boolean {
  return item.group_id === null || item.group_deleted;
}

export function inGroup(item: Pick<Ingredient, 'group_id' | 'group_deleted'>, group: GroupFilter): boolean {
  if (group === 'all') return true;
  if (group === 'none') return hasNoGroup(item);
  return item.group_id === group && !item.group_deleted;
}

export function filterIngredients(
  items: Ingredient[],
  { segment, group, search }: { segment: IngredientSegment; group: GroupFilter; search: string },
): Ingredient[] {
  const tokens = searchTokens(search);
  return items.filter((it) => inSegment(it, segment) && inGroup(it, group) && matchesSearch(it, tokens));
}

// ── Per-unit price ───────────────────────────────────────────────────────

const PER_KG = new Set(['g', 'gram', 'grams', 'gramme', 'grammes']);
const PER_L = new Set(['ml', 'millilitre', 'millilitres', 'milliliter', 'milliliters']);
const KG = new Set(['kg', 'kilo', 'kilos', 'kilogram', 'kilograms', 'kilogramme', 'kilogrammes']);
const L = new Set(['l', 'litre', 'litres', 'liter', 'liters']);
const EA = new Set(['ea', 'each', 'unit', 'units', 'piece', 'pieces']);

export type PerUnit = { value: number; unit: string };

/**
 * What one kg, one L or one each costs: the API's price_per_unit is per the
 * ingredient's own unit (per gram for most), and a chef reads $/kg. Null
 * when the figure means nothing: no price set, or no pack size (the engine
 * then costs one pack as one unit, B1). Any other unit (tsp, cup, a
 * kitchen's own) keeps its own name.
 */
export function perUnit(
  item: Pick<Ingredient, 'unit' | 'price_per_unit' | 'price_is_set' | 'size_is_set'>,
): PerUnit | null {
  if (!item.price_is_set || !item.size_is_set) return null;
  if (!Number.isFinite(item.price_per_unit)) return null;
  const u = (item.unit || '').trim().toLowerCase();
  if (PER_KG.has(u)) return { value: item.price_per_unit * 1000, unit: 'kg' };
  if (PER_L.has(u)) return { value: item.price_per_unit * 1000, unit: 'L' };
  if (KG.has(u)) return { value: item.price_per_unit, unit: 'kg' };
  if (L.has(u)) return { value: item.price_per_unit, unit: 'L' };
  if (EA.has(u)) return { value: item.price_per_unit, unit: 'ea' };
  return { value: item.price_per_unit, unit: item.unit };
}

// ── Status chip ──────────────────────────────────────────────────────────

export type NeedsChip = {
  /** The DS StatusBadge kind (every one is the estimate amber). */
  kind: 'needs-price' | 'needs-size' | 'needs-both';
  label: string;
  /** Everything it needs, in fix order, for the tooltip and screen readers. */
  title: string;
};

const NEED_WORDS: Record<IngredientNeed, string> = {
  price: 'a price',
  size: 'a pack size',
  density: 'a density',
  each_weight: 'the weight of one',
};

/** The chip for an ingredient whose own cost is an estimate, from the API's
 *  `needs` (already in fix order). Price and size are the handoff's words;
 *  density and the weight of one get their own so a chef knows what to fix. */
export function needsChip(needs: readonly IngredientNeed[]): NeedsChip | null {
  if (needs.length === 0) return null;
  const words = needs.map((n) => NEED_WORDS[n]);
  const title = `Needs ${words.length > 1 ? `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}` : words[0]}. Dishes that use it are estimates until then.`;
  const price = needs.includes('price');
  const size = needs.includes('size');
  if (price && size) return { kind: 'needs-both', label: 'Needs price and size', title };
  if (price) return { kind: 'needs-price', label: 'Needs price', title };
  if (size) return { kind: 'needs-size', label: 'Needs size', title };
  if (needs[0] === 'density') return { kind: 'needs-price', label: 'Needs density', title };
  return { kind: 'needs-price', label: 'Needs weight of one', title };
}

// ── Sorting ──────────────────────────────────────────────────────────────

export type IngredientSort =
  | 'name-asc' | 'name-desc'
  | 'group-asc' | 'supplier-asc'
  | 'pack-desc' | 'pack-asc'
  | 'unit-desc' | 'unit-asc'
  | 'used-desc' | 'used-asc'
  | 'newest';

export const DEFAULT_SORT: IngredientSort = 'name-asc';

type Key = string | number | null;

const byName = (a: Ingredient, b: Ingredient) =>
  a.name.localeCompare(b.name, undefined, { sensitivity: 'base', numeric: true }) ||
  (a.variant ?? '').localeCompare(b.variant ?? '', undefined, { sensitivity: 'base', numeric: true }) ||
  a.id - b.id;

/** Sort by a key, blanks last in both directions, ties by name. */
function byKey(key: (i: Ingredient) => Key, desc: boolean) {
  return (a: Ingredient, b: Ingredient) => {
    const x = key(a);
    const y = key(b);
    const xb = x === null || x === '';
    const yb = y === null || y === '';
    if (xb || yb) return xb && yb ? byName(a, b) : xb ? 1 : -1;
    let c = 0;
    if (typeof x === 'number' && typeof y === 'number') c = x - y;
    else c = String(x).localeCompare(String(y), undefined, { sensitivity: 'base', numeric: true });
    if (desc) c = -c;
    return c || byName(a, b);
  };
}

const groupKey = (i: Ingredient): Key => (hasNoGroup(i) ? null : i.group_name);
const packKey = (i: Ingredient): Key => (i.price_is_set ? i.package_price : null);
const unitKey = (i: Ingredient): Key => perUnit(i)?.value ?? null;
const usedKey = (i: Ingredient): Key => i.consumers.length;

export function sortIngredients(items: Ingredient[], sort: IngredientSort): Ingredient[] {
  const out = [...items];
  switch (sort) {
    case 'name-asc':
      return out.sort(byName);
    case 'name-desc':
      return out.sort((a, b) => byName(b, a));
    case 'group-asc':
      return out.sort(byKey(groupKey, false));
    case 'supplier-asc':
      return out.sort(byKey((i) => i.supplier, false));
    case 'pack-desc':
      return out.sort(byKey(packKey, true));
    case 'pack-asc':
      return out.sort(byKey(packKey, false));
    case 'unit-desc':
      return out.sort(byKey(unitKey, true));
    case 'unit-asc':
      return out.sort(byKey(unitKey, false));
    case 'used-desc':
      return out.sort(byKey(usedKey, true));
    case 'used-asc':
      return out.sort(byKey(usedKey, false));
    case 'newest':
      return out.sort((a, b) => {
        const x = a.created_at ?? '';
        const y = b.created_at ?? '';
        if (!x || !y) return x ? -1 : y ? 1 : b.id - a.id;
        return y.localeCompare(x) || b.id - a.id;
      });
  }
}

// ── Sortable headers ─────────────────────────────────────────────────────

/** The columns whose header sorts (the handoff's Name, Per unit and Used),
 *  and the sort each direction means. Others sort from the Sort menu. */
const HEADER_SORTS: Record<string, { asc: IngredientSort; desc: IngredientSort }> = {
  name: { asc: 'name-asc', desc: 'name-desc' },
  per_unit: { asc: 'unit-asc', desc: 'unit-desc' },
  used: { asc: 'used-asc', desc: 'used-desc' },
};

export const SORTABLE_COLUMNS = Object.keys(HEADER_SORTS);

/** The table's arrow for a sort: the header it belongs to, if any. */
export function sortToSorting(sort: IngredientSort): { id: string; desc: boolean }[] {
  for (const [id, dirs] of Object.entries(HEADER_SORTS)) {
    if (dirs.asc === sort) return [{ id, desc: false }];
    if (dirs.desc === sort) return [{ id, desc: true }];
  }
  return [];
}

/** A header click back to a sort. Clearing the sort (a third click) returns
 *  to the default, so the list is never left in no order at all. */
export function sortingToSort(sorting: readonly { id: string; desc: boolean }[]): IngredientSort {
  const s = sorting[0];
  const dirs = s ? HEADER_SORTS[s.id] : undefined;
  if (!s || !dirs) return DEFAULT_SORT;
  return s.desc ? dirs.desc : dirs.asc;
}
